import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { RestBuilder } from '../helpers/rest.builder';
import { SKIP_FORBIDDEN_PAGE } from '../../shared/api';
import {
  IAssignUsersRequest,
  IBulkUpdateUsersSecurityGroupsRequest,
  ICreateUsersRequest,
  ISecurityGroupsWithSecurityGroupUserLastUpdatedInTenant,
  ISecurityUserWithGroupsQuery,
  ISecurityUserWithGroupsResponse,
} from '../../../../shared/interfaces/security-group.interface';
import {
  IAssignUsersResponse,
  IGetOrCreateUserResponse,
  IUsersServerResponse,
} from '../../../../shared/interfaces/user.interface';
import { IEmailLookupRequest, IEmailLookupResponse } from '../../../../shared/interfaces/email-lookup.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { ApiQueryOption } from '../../../../shared/constants/api';
import { IBulkAddToStaffGroupsResponse } from '../../../../shared/interfaces/security.interface';
import { $securityEntity, $securityGroupUser, SecurityEntityLevel } from '../../../../shared/constants/security-group';
import { FileExtension } from '../../../../shared/constants/file-extension';
import { IUserListReportQuery, IUserListReportResponse } from '../../../../shared/interfaces/async-report.interface';
import { IUserListFilterOptionsResponse } from '../../../../shared/interfaces/user-list.interface';
import {
  IBulkJobFunctionWriteRequest,
  IBulkJobFunctionWriteResult,
  IBulkRemovalCheckResult,
  IBulkRemoveRequest,
  IBulkRemoveResult,
  IBulkPermissionWriteRequest,
  IBulkPermissionWriteResult,
  IBulkUserGrantsSummary,
  IScopedPermissionWriteRequest,
  IScopedPermissionWriteResult,
  IUserDetailsWriteResult,
  IUserGrantsSummary,
} from '../../../../shared/interfaces/permission-write.interface';
import { IPermissionTreeEntity } from '../../../../shared/interfaces/permission-tree.interface';

@Injectable({
  providedIn: 'root',
})
export class SecurityUsersApi {
  private restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  // GET /admin/security/users
  public findUsersWithSecurityGroupsByTenantId(
    options: ISecurityUserWithGroupsQuery,
  ): Observable<ISecurityUserWithGroupsResponse> {
    const url: string = this.restBuilder.create().admin().security().users().getApiUrl();

    let params: HttpParams = this.restBuilder.paginationParams(options.page ?? 0, options.limit ?? 0);

    if (SharedCommonUtility.notNullishOrEmpty(options.tenantId)) {
      params = params.set($securityEntity.tenantId, options.tenantId);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.searchTerm)) {
      params = params.set(ApiQueryOption.term, options.searchTerm);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.workspaceId)) {
      params = params.set($securityEntity.workspaceId, options.workspaceId);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.digitalPropertyId)) {
      params = params.set($securityEntity.digitalPropertyId, options.digitalPropertyId);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.entityLevel)) {
      params = params.set(ApiQueryOption.entityLevel, options.entityLevel);
    }

    if (SharedCommonUtility.notNullish(options.isStaffGroup)) {
      params = params.set(ApiQueryOption.isStaffGroup, options.isStaffGroup);
    }

    if (SharedCommonUtility.notNullish(options.isServiceGroup)) {
      params = params.set(ApiQueryOption.isServiceGroup, options.isServiceGroup);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.securityGroupId)) {
      params = params.set($securityGroupUser.securityGroupId, options.securityGroupId);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.excludeGroupIds)) {
      for (const id of options.excludeGroupIds) {
        params = params.append(ApiQueryOption.excludeGroupIds, id);
      }
    }

    for (const id of options.workspaceIds ?? []) {
      params = params.append(ApiQueryOption.workspaceIds, id);
    }

    for (const id of options.digitalPropertyIds ?? []) {
      params = params.append(ApiQueryOption.digitalPropertyIds, id);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.sortBy)) {
      params = params.set(ApiQueryOption.sortBy, options.sortBy);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.sortOrder)) {
      params = params.set(ApiQueryOption.sortOrder, options.sortOrder);
    }

    if (SharedCommonUtility.notNullishOrEmpty(options.inviteStatus)) {
      params = params.set(ApiQueryOption.inviteStatus, options.inviteStatus);
    }

    return this.httpClient.get<ISecurityUserWithGroupsResponse>(url, { params });
  }

  // GET /admin/security/users/filter-options/workspaces
  public getWorkspaceFilterOptions(term: string, page: number, limit: number): Observable<IUserListFilterOptionsResponse> {
    const url: string = this.restBuilder.create().admin().security().users().filterOptions().workspaces().getApiUrl();

    let params: HttpParams = this.restBuilder.paginationParams(page, limit);

    if (SharedCommonUtility.notNullishOrEmpty(term)) {
      params = params.set(ApiQueryOption.term, term);
    }

    return this.httpClient.get<IUserListFilterOptionsResponse>(url, { params });
  }

  // GET /admin/security/users/filter-options/digital-properties
  public getDigitalAssetFilterOptions(
    term: string,
    page: number,
    limit: number,
    workspaceIds: string[] = [],
    ids: string[] = [],
  ): Observable<IUserListFilterOptionsResponse> {
    const url: string = this.restBuilder.create().admin().security().users().filterOptions().digitalAssets().getApiUrl();

    let params: HttpParams = this.restBuilder.paginationParams(page, limit);

    if (SharedCommonUtility.notNullishOrEmpty(term)) {
      params = params.set(ApiQueryOption.term, term);
    }

    for (const workspaceId of workspaceIds) {
      params = params.append(ApiQueryOption.workspaceIds, workspaceId);
    }

    for (const id of ids) {
      params = params.append(ApiQueryOption.ids, id);
    }

    return this.httpClient.get<IUserListFilterOptionsResponse>(url, { params });
  }

  // GET /admin/security/users/unassigned-members
  public getUnassignedUsers(limit: number, page: number, searchTerm?: string): Observable<IUsersServerResponse> {
    const url: string = this.restBuilder.create().admin().security().users().unassignedMembers().getApiUrl();

    let params: HttpParams = this.restBuilder.paginationParams(page, limit);

    if (SharedCommonUtility.notNullishOrEmpty(searchTerm)) {
      params = params.set(ApiQueryOption.term, searchTerm);
    }

    return this.httpClient.get<IUsersServerResponse>(url, { params });
  }

  // PUT /admin/security/users/assign-users
  public assignSecurityUsers(request: IAssignUsersRequest): Observable<IAssignUsersResponse> {
    const url: string = this.restBuilder.create().admin().security().users().assignUsers().getApiUrl();
    return this.httpClient.put<IAssignUsersResponse>(url, request);
  }

  // POST /admin/security/users
  public createSecurityUsers(request: ICreateUsersRequest): Observable<IGetOrCreateUserResponse> {
    const url: string = this.restBuilder.create().admin().security().users().getApiUrl();
    return this.httpClient.post<IGetOrCreateUserResponse>(url, request, { context: SecurityUsersApi.permissionsContext() });
  }

  // POST /admin/security/users/email-lookup
  public lookupEmails(request: IEmailLookupRequest): Observable<IEmailLookupResponse> {
    const url: string = this.restBuilder.create().admin().security().users().emailLookup().getApiUrl();
    return this.httpClient.post<IEmailLookupResponse>(url, request, { context: SecurityUsersApi.permissionsContext() });
  }

  // DELETE /admin/security/users/:userId
  public removeUser(userId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().security().users(userId).getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  // GET /admin/security/users/removal-check/bulk
  public checkBulkRemoval(userIds: string[]): Observable<IBulkRemovalCheckResult> {
    let params: HttpParams = new HttpParams();
    for (const userId of userIds) {
      params = params.append(ApiQueryOption.userIds, userId);
    }

    const url: string = this.restBuilder.create().admin().security().users().removalCheck().bulk().getApiUrl();
    return this.httpClient.get<IBulkRemovalCheckResult>(url, { params });
  }

  // DELETE /admin/security/users/bulk
  public bulkRemoveUsers(request: IBulkRemoveRequest): Observable<IBulkRemoveResult> {
    const url: string = this.restBuilder.create().admin().security().users().bulk().getApiUrl();
    return this.httpClient.delete<IBulkRemoveResult>(url, { body: request });
  }

  // GET /admin/security/users/:userId/tenants-membership
  public getSecurityUserTenantsMembership(userId: string): Observable<ISecurityGroupsWithSecurityGroupUserLastUpdatedInTenant[]> {
    const url: string = this.restBuilder.create().admin().security().users(userId).tenantsMembership().getApiUrl();
    return this.httpClient.get<ISecurityGroupsWithSecurityGroupUserLastUpdatedInTenant[]>(url);
  }

  // POST /admin/security/users/:userId/groups
  public addUserToSecurityGroups(userId: string, groupIds: string[]): Observable<void> {
    const url: string = this.restBuilder.create().admin().security().users(userId).groups().getApiUrl();
    return this.httpClient.post<void>(url, { groupIds });
  }

  // POST /admin/security/users/:userId/staffGroups
  public bulkAddToStaffSecurityGroups(
    userId: string,
    staffSecurityName: string,
    tenantIds: string[],
  ): Observable<IBulkAddToStaffGroupsResponse> {
    const url: string = this.restBuilder.create().admin().security().users(userId).staffGroups().getApiUrl();
    return this.httpClient.post<IBulkAddToStaffGroupsResponse>(url, { staffSecurityName, tenantIds });
  }

  // PUT /admin/security/users/:userId
  public updateUserProfile(userProfile: FormData, userId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().security().users(userId).getApiUrl();
    return this.httpClient.put<void>(url, userProfile);
  }

  // PUT /admin/security/users/:userId/details
  public updateUserDetails(userId: string, request: FormData): Observable<IUserDetailsWriteResult> {
    const url: string = this.restBuilder.create().admin().security().users(userId).details().getApiUrl();
    return this.httpClient.put<IUserDetailsWriteResult>(url, request);
  }

  // PUT /admin/security/users/:userId/groups
  public updateUserSecurityGroups(userId: string, groupIds: string[], tenantId?: string): Observable<void> {
    let params: HttpParams = new HttpParams();

    if (SharedCommonUtility.notNullishOrEmpty(tenantId)) {
      params = params.set($securityEntity.tenantId, tenantId);
    }

    const url: string = this.restBuilder.create().admin().security().users(userId).groups().getApiUrl();
    return this.httpClient.put<void>(url, { groupIds }, { params });
  }

  // PUT /admin/security/users/groups/bulk
  public bulkUpdateUsersSecurityGroups(request: IBulkUpdateUsersSecurityGroupsRequest): Observable<void> {
    let params: HttpParams = new HttpParams();

    if (SharedCommonUtility.notNullishOrEmpty(request.tenantId)) {
      params = params.set($securityEntity.tenantId, request.tenantId);
    }

    const url: string = this.restBuilder.create().admin().security().users().groups().bulk().getApiUrl();
    return this.httpClient.put<void>(url, request, { params });
  }

  // DELETE /admin/security/users/:userId/groups
  public removeUserFromSecurityGroups(userId: string, groupIds: string[]): Observable<void> {
    const url: string = this.restBuilder.create().admin().security().users(userId).groups().getApiUrl();
    return this.httpClient.delete<void>(url, { body: { groupIds } });
  }

  /** A refused permissions request is explained inline in the slideout, not by navigating away to the 403 page. */
  private static permissionsContext(): HttpContext {
    return new HttpContext().set(SKIP_FORBIDDEN_PAGE, true);
  }

  // GET /admin/security/users/permission-tree
  public getPermissionTree(): Observable<IPermissionTreeEntity[]> {
    const url: string = this.restBuilder.create().admin().security().users().permissionTree().getApiUrl();
    return this.httpClient.get<IPermissionTreeEntity[]>(url, { context: SecurityUsersApi.permissionsContext() });
  }

  // GET /admin/security/users/:userId/permissions
  public getUserPermissions(userId: string): Observable<IUserGrantsSummary> {
    const url: string = this.restBuilder.create().admin().security().users(userId).permissions().getApiUrl();
    return this.httpClient.get<IUserGrantsSummary>(url, { context: SecurityUsersApi.permissionsContext() });
  }

  // PUT /admin/security/users/:userId/permissions
  public replaceUserPermissions(
    userId: string,
    request: IScopedPermissionWriteRequest,
  ): Observable<IScopedPermissionWriteResult> {
    const url: string = this.restBuilder.create().admin().security().users(userId).permissions().getApiUrl();
    return this.httpClient.put<IScopedPermissionWriteResult>(url, request, { context: SecurityUsersApi.permissionsContext() });
  }

  // PUT /admin/security/users/job-function/bulk
  public bulkUpdateJobFunction(request: IBulkJobFunctionWriteRequest): Observable<IBulkJobFunctionWriteResult> {
    const url: string = this.restBuilder.create().admin().security().users().jobFunction().bulk().getApiUrl();
    return this.httpClient.put<IBulkJobFunctionWriteResult>(url, request);
  }

  // GET /admin/security/users/permissions/bulk
  public getBulkUserPermissions(userIds: string[]): Observable<IBulkUserGrantsSummary> {
    const url: string = this.restBuilder.create().admin().security().users().permissions().bulk().getApiUrl();
    const params: HttpParams = userIds.reduce(
      (acc: HttpParams, userId: string): HttpParams => acc.append(ApiQueryOption.userIds, userId),
      new HttpParams(),
    );
    return this.httpClient.get<IBulkUserGrantsSummary>(url, { params, context: SecurityUsersApi.permissionsContext() });
  }

  // PUT /admin/security/users/permissions/bulk
  public bulkReplaceUserPermissions(request: IBulkPermissionWriteRequest): Observable<IBulkPermissionWriteResult> {
    const url: string = this.restBuilder.create().admin().security().users().permissions().bulk().getApiUrl();
    return this.httpClient.put<IBulkPermissionWriteResult>(url, request, { context: SecurityUsersApi.permissionsContext() });
  }

  // POST /admin/security/users/:userId/resend-invite
  public resendInvite(userId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().security().users(userId).resendInvite().getApiUrl();
    return this.httpClient.post<void>(url, null);
  }

  // GET /admin/security/users/report
  public downloadReport(
    fileExtension: FileExtension,
    workspaceId?: string,
    digitalPropertyId?: string,
  ): Observable<IUserListReportResponse> {
    const url: string = this.restBuilder.create().admin().security().users().report().getApiUrl();
    const params: Record<string, string> = {
      [ApiQueryOption.format]: fileExtension,
    };

    if (SharedCommonUtility.notNullishOrEmpty(workspaceId)) {
      params[ApiQueryOption.workspaceId] = workspaceId;
    }

    if (SharedCommonUtility.notNullishOrEmpty(digitalPropertyId)) {
      params[ApiQueryOption.digitalPropertyId] = digitalPropertyId;
    }

    const httpParams: HttpParams = new HttpParams({ fromObject: params });
    return this.httpClient.get<IUserListReportResponse>(url, { params: httpParams });
  }

  // POST /admin/security/users/report
  public generateReport(
    fileExtension: FileExtension,
    workspaceId?: string,
    digitalPropertyId?: string,
    entityLevel?: SecurityEntityLevel,
  ): Observable<IUserListReportResponse> {
    const url: string = this.restBuilder.create().admin().security().users().report().getApiUrl();
    const request: IUserListReportQuery = {
      [ApiQueryOption.format]: fileExtension,
    };

    if (SharedCommonUtility.notNullishOrEmpty(workspaceId)) {
      request[ApiQueryOption.workspaceId] = workspaceId;
    }

    if (SharedCommonUtility.notNullishOrEmpty(digitalPropertyId)) {
      request[ApiQueryOption.digitalPropertyId] = digitalPropertyId;
    }

    if (SharedCommonUtility.notNullishOrEmpty(entityLevel)) {
      request[ApiQueryOption.entityLevel] = entityLevel;
    }

    return this.httpClient.post<IUserListReportResponse>(url, request);
  }
}
